import { useCallback, useState } from 'react';
import { Tab, Divider, Tabs, TabsProps } from '@mui/material';
import { useIntl } from 'react-intl';
import { Route, Routes } from 'react-router-dom';

import { useConfiguration } from '@ocx/data-configuration';
import { NavigationBar, Page } from '@ocx/ui';
import { ReceiptController } from '@ocx/feat-pay-at-pump';
import { TabPanel } from './components/tab-panel.component';
import { BackButton } from '../../components/Layout/NavigationBar/BackButton/BackButton';
import { messages } from './transaction-history.messages';
import { MobileTransactionsListController } from './mobile-transactions-list.controller';
import { LoyaltyActionsListController } from './loyalty-actions-list.controller';
import { HtmlTitle } from '../../components/router/HtmlTitle';
import { TransactionReceiptController } from '../TransactionReceipt/TransactionReceiptController';
import { PointsChangeReceiptController } from '../PointsChangeReceipt/PointsChangeReceiptController';
import { AppRoutePath } from '../AppRoutePath';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';

export type TransactionHistoryTab = 'mobile' | 'loyalty';

export const TransactionHistory = () => {
  const intl = useIntl();
  const [selectedTab, setSelectedTab] = useState<TransactionHistoryTab>('loyalty');
  const { triggerEvent } = useRudderStack();
  const isMobPaymentsReceiptsEnabled = useConfiguration('mobilePaymentsReceiptsView.enabled');

  const triggerAnalytics = useCallback(
    (tab: TransactionHistoryTab) => {
      if (tab === 'mobile') {
        triggerEvent('transaction_history_receipts_tab_clicked');
      }
      if (tab === 'loyalty') {
        triggerEvent('transaction_history_loyalty_transactions_tab_clicked');
      }
    },
    [triggerEvent],
  );

  const handleTabChange = useCallback<NonNullable<TabsProps['onChange']>>(
    (event, value) => {
      triggerAnalytics(value);
      setSelectedTab(value);
    },
    [triggerAnalytics],
  );

  return (
    <Page>
      <NavigationBar left={<BackButton />} center="Receipts" />
      {isMobPaymentsReceiptsEnabled && (
        <>
          <Tabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
            <Tab
              value="loyalty"
              label={intl.formatMessage(messages['transaction-history:loyalty-activities:tab-title'])}
            />
            <Tab value="mobile" label={intl.formatMessage(messages['transaction-history:transactions:tab-title'])} />
          </Tabs>
          <Divider />
        </>
      )}
      <TabPanel value={selectedTab} name="loyalty">
        <HtmlTitle title={messages['transaction-history:loyalty-activities:html-title']} />
        <LoyaltyActionsListController />
      </TabPanel>
      {isMobPaymentsReceiptsEnabled && (
        <TabPanel value={selectedTab} name="mobile">
          <HtmlTitle title={messages['transaction-history:transactions:html-title']} />
          <MobileTransactionsListController />
        </TabPanel>
      )}
      <Routes>
        <Route path={AppRoutePath.TransactionActivateDetailsNested} element={<TransactionReceiptController />} />
        <Route path={AppRoutePath.PointsChangeActivateDetailsNested} element={<PointsChangeReceiptController />} />
        <Route
          path={AppRoutePath.TransactionMobileDetailsNested}
          element={<ReceiptController modal gratitudeLabelHidden />}
        />
      </Routes>
    </Page>
  );
};
